import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus,
  Upload,
  Users,
  CheckCircle,
  XCircle,
  UserMinus,
  RefreshCw,
  Search,
  Pencil,
  Trash2,
  Settings,
  X,
  Loader2,
} from 'lucide-react';
import { ToastContainer, useToast } from '@/shared/components/ui/Toast';
import { CuteConfirmModal } from '@/shared/components/CuteConfirmModal';
import {
  listLeads,
  createLead,
  updateLead,
  deleteLead,
  getLeadStats,
  importFormerUsers,
  listFields,
  MarketingLead,
  MarketingLeadField,
  CreateLeadDto,
  LeadFilters,
  LeadStats,
  sourceLabel,
  statusColor,
  statusLabel,
  MarketingLeadStatus,
} from '@/services/marketingLeadService';
import { CsvImportWizard } from './components/CsvImportWizard';
import { FieldDefinitionsModal } from './components/FieldDefinitionsModal';

const emptyForm: CreateLeadDto = {
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  company: '',
  jobTitle: '',
  city: '',
  country: '',
  tags: [],
  customFields: {},
};

export const ContactsPage: React.FC = () => {
  const { toasts, removeToast, success, error } = useToast();

  const [leads, setLeads] = useState<MarketingLead[]>([]);
  const [stats, setStats] = useState<LeadStats | null>(null);
  const [fields, setFields] = useState<MarketingLeadField[]>([]);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<MarketingLeadStatus | ''>('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editing, setEditing] = useState<MarketingLead | null>(null);
  const [formData, setFormData] = useState<CreateLeadDto>(emptyForm);
  const [saving, setSaving] = useState(false);

  const [showImport, setShowImport] = useState(false);
  const [showFieldDefs, setShowFieldDefs] = useState(false);
  const [importingFormer, setImportingFormer] = useState(false);

  // Single confirm-state shared by all destructive / important actions
  const [confirmState, setConfirmState] = useState<{
    title: string;
    message: string;
    itemName?: string;
    confirmLabel: string;
    variant: 'danger' | 'warning' | 'info' | 'success';
    onConfirm: () => Promise<void> | void;
  } | null>(null);

  const fetchAll = async (overrides: Partial<LeadFilters> = {}) => {
    setLoading(true);
    try {
      const [page, sts, flds] = await Promise.all([
        listLeads({
          search: overrides.search ?? search,
          status: (overrides.status as MarketingLeadStatus) ?? (statusFilter || undefined),
          page: overrides.page ?? meta.page,
          limit: meta.limit,
        }),
        getLeadStats(),
        listFields(true),
      ]);
      setLeads(page.data);
      setMeta(page.meta);
      setStats(sts);
      setFields(flds);
    } catch (err: any) {
      console.error(err);
      error('Erreur', err?.message ?? 'Impossible de charger les leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchAll({ page: 1 }), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  const openCreate = () => {
    setEditing(null);
    setFormData(emptyForm);
    setShowCreateModal(true);
  };

  const openEdit = (l: MarketingLead) => {
    setEditing(l);
    setFormData({
      email: l.email,
      firstName: l.firstName ?? '',
      lastName: l.lastName ?? '',
      phone: l.phone ?? '',
      company: l.company ?? '',
      jobTitle: l.jobTitle ?? '',
      city: l.city ?? '',
      country: l.country ?? '',
      tags: l.tags ?? [],
      customFields: l.customFields ?? {},
    });
    setShowCreateModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.email.trim()) return error('Email requis', '');
    setSaving(true);
    try {
      if (editing) {
        await updateLead(editing.id, formData);
        success('Lead mis à jour', '');
      } else {
        await createLead(formData);
        success('Lead créé', '');
      }
      setShowCreateModal(false);
      fetchAll();
    } catch (err: any) {
      error('Erreur', err?.message ?? 'Échec');
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (l: MarketingLead) => {
    setConfirmState({
      title: 'Supprimer ce lead',
      message: 'Cette action est définitive — toutes ses données seront effacées.',
      itemName: l.email,
      confirmLabel: 'Supprimer',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await deleteLead(l.id);
          success('Lead supprimé', '');
          fetchAll();
        } catch (err: any) {
          error('Erreur', err?.message ?? 'Suppression impossible');
        }
      },
    });
  };

  const askImportFormerUsers = () => {
    setConfirmState({
      title: 'Importer les anciens utilisateurs',
      message:
        'Les comptes DG, managers, coordinateurs inactifs seront ajoutés à votre liste de leads avec le tag « ex-utilisateur ».',
      confirmLabel: 'Importer',
      variant: 'info',
      onConfirm: async () => {
        setImportingFormer(true);
        try {
          const res = await importFormerUsers();
          success(
            'Import terminé',
            `${res.imported} ajouté(s), ${res.skipped} ignoré(s) sur ${res.candidates} candidats.`,
          );
          fetchAll();
        } catch (err: any) {
          error('Erreur', err?.message ?? "Échec de l'import");
        } finally {
          setImportingFormer(false);
        }
      },
    });
  };

  const customFieldsForForm = useMemo(
    () => fields.filter((f) => f.isActive),
    [fields],
  );

  const updateCustom = (key: string, value: unknown) =>
    setFormData((prev) => ({
      ...prev,
      customFields: { ...(prev.customFields ?? {}), [key]: value },
    }));

  return (
    <>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
            <p className="text-sm text-gray-500 mt-1">
              Prospects et anciens utilisateurs ciblés par vos campagnes marketing.
              Les utilisateurs actuels de la plateforme ne reçoivent jamais ces emails.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* Tertiary: ghost icon-only with hover labels */}
            <button
              onClick={() => setShowFieldDefs(true)}
              className="inline-flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition whitespace-nowrap"
              title="Gérer les champs personnalisés"
            >
              <Settings size={16} />
              <span className="text-sm">Champs</span>
            </button>
            <button
              onClick={askImportFormerUsers}
              disabled={importingFormer}
              className="inline-flex items-center gap-2 px-3 py-2 text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition disabled:opacity-50 whitespace-nowrap"
              title="Ajouter les anciens utilisateurs comme leads"
            >
              {importingFormer ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              <span className="text-sm font-medium">Anciens utilisateurs</span>
            </button>

            {/* Secondary: outlined indigo */}
            <button
              onClick={() => setShowImport(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-indigo-700 bg-white border border-indigo-200 rounded-lg hover:bg-indigo-50 transition whitespace-nowrap"
            >
              <Upload size={16} />
              <span className="text-sm font-medium">Importer Excel / CSV</span>
            </button>

            {/* Primary: gradient CTA */}
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 rounded-lg shadow-sm hover:shadow-md transition whitespace-nowrap font-medium text-sm"
            >
              <Plus size={16} />
              Ajouter un lead
            </button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-4 gap-4">
            <StatCard
              icon={<Users className="text-indigo-600" size={20} />}
              bg="bg-indigo-100"
              label="Total leads"
              value={stats.total}
            />
            <StatCard
              icon={<CheckCircle className="text-green-600" size={20} />}
              bg="bg-green-100"
              label="Actifs"
              value={stats.active}
            />
            <StatCard
              icon={<UserMinus className="text-amber-600" size={20} />}
              bg="bg-amber-100"
              label="Anciens utilisateurs"
              value={stats.formerUsers}
            />
            <StatCard
              icon={<XCircle className="text-gray-500" size={20} />}
              bg="bg-gray-100"
              label="Désinscrits"
              value={stats.unsubscribed}
            />
          </div>
        )}

        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par email, nom, société…"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as MarketingLeadStatus | '')}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white"
          >
            <option value="">Tous les statuts</option>
            <option value="ACTIVE">Actifs</option>
            <option value="UNSUBSCRIBED">Désinscrits</option>
            <option value="BOUNCED">Email invalide</option>
          </select>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Nom</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Société</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Source</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Statut</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Tags</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-gray-400">
                      <Loader2 className="inline animate-spin" />
                    </td>
                  </tr>
                ) : leads.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-gray-500">
                      Aucun lead trouvé.
                    </td>
                  </tr>
                ) : (
                  leads.map((l) => (
                    <tr key={l.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{l.email}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {l.fullName || `${l.firstName ?? ''} ${l.lastName ?? ''}`.trim() || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{l.company ?? '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">{sourceLabel(l.source)}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(l.status)}`}
                        >
                          {statusLabel(l.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {l.tags.length > 0 ? l.tags.join(', ') : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1">
                          <button
                            onClick={() => openEdit(l)}
                            className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded"
                            title="Modifier"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => askDelete(l)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title="Supprimer"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {meta.totalPages > 1 && (
            <div className="flex justify-between items-center px-4 py-3 border-t border-gray-100 text-sm">
              <span className="text-gray-600">
                Page {meta.page} sur {meta.totalPages} — {meta.total} lead(s)
              </span>
              <div className="space-x-2">
                <button
                  onClick={() => fetchAll({ page: Math.max(1, meta.page - 1) })}
                  disabled={meta.page <= 1}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
                >
                  Précédent
                </button>
                <button
                  onClick={() => fetchAll({ page: Math.min(meta.totalPages, meta.page + 1) })}
                  disabled={meta.page >= meta.totalPages}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-lg font-semibold text-gray-900">
                {editing ? 'Modifier le lead' : 'Ajouter un lead'}
              </h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1.5 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  disabled={!!editing}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                />
                {!editing && (
                  <p className="text-xs text-gray-500 mt-1">
                    Si cet email est déjà un utilisateur de la plateforme, l'ajout sera refusé.
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FieldInput
                  label="Prénom"
                  value={formData.firstName ?? ''}
                  onChange={(v) => setFormData({ ...formData, firstName: v })}
                />
                <FieldInput
                  label="Nom"
                  value={formData.lastName ?? ''}
                  onChange={(v) => setFormData({ ...formData, lastName: v })}
                />
                <FieldInput
                  label="Téléphone"
                  value={formData.phone ?? ''}
                  onChange={(v) => setFormData({ ...formData, phone: v })}
                />
                <FieldInput
                  label="Société"
                  value={formData.company ?? ''}
                  onChange={(v) => setFormData({ ...formData, company: v })}
                />
                <FieldInput
                  label="Fonction"
                  value={formData.jobTitle ?? ''}
                  onChange={(v) => setFormData({ ...formData, jobTitle: v })}
                />
                <FieldInput
                  label="Ville"
                  value={formData.city ?? ''}
                  onChange={(v) => setFormData({ ...formData, city: v })}
                />
                <FieldInput
                  label="Pays"
                  value={formData.country ?? ''}
                  onChange={(v) => setFormData({ ...formData, country: v })}
                />
                <FieldInput
                  label="Tags (séparés par des virgules)"
                  value={(formData.tags ?? []).join(', ')}
                  onChange={(v) =>
                    setFormData({
                      ...formData,
                      tags: v.split(',').map((t) => t.trim()).filter(Boolean),
                    })
                  }
                />
              </div>

              {customFieldsForForm.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    Champs personnalisés
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    {customFieldsForForm.map((f) => {
                      const cf = formData.customFields ?? {};
                      const val = cf[f.key];
                      return (
                        <div key={f.id}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {f.label}{f.required ? ' *' : ''}
                          </label>
                          {f.type === 'BOOLEAN' ? (
                            <select
                              value={val == null ? '' : String(val)}
                              onChange={(e) =>
                                updateCustom(
                                  f.key,
                                  e.target.value === '' ? null : e.target.value === 'true',
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                            >
                              <option value="">—</option>
                              <option value="true">Oui</option>
                              <option value="false">Non</option>
                            </select>
                          ) : f.type === 'SELECT' ? (
                            <select
                              value={val == null ? '' : String(val)}
                              onChange={(e) => updateCustom(f.key, e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                            >
                              <option value="">—</option>
                              {(f.options ?? []).map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type={f.type === 'NUMBER' ? 'number' : f.type === 'DATE' ? 'date' : 'text'}
                              value={val == null ? '' : String(val)}
                              onChange={(e) =>
                                updateCustom(
                                  f.key,
                                  f.type === 'NUMBER' && e.target.value !== ''
                                    ? Number(e.target.value)
                                    : e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-gray-200 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                {editing ? 'Enregistrer' : 'Créer le lead'}
              </button>
            </div>
          </div>
        </div>
      )}

      <CsvImportWizard
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => {
          setShowImport(false);
          fetchAll();
        }}
      />

      <FieldDefinitionsModal
        open={showFieldDefs}
        onClose={() => setShowFieldDefs(false)}
        onChanged={() => fetchAll()}
      />

      {confirmState && (
        <CuteConfirmModal
          isOpen
          title={confirmState.title}
          message={confirmState.message}
          itemName={confirmState.itemName}
          confirmLabel={confirmState.confirmLabel}
          variant={confirmState.variant}
          onConfirm={async () => {
            const action = confirmState.onConfirm;
            setConfirmState(null);
            await action();
          }}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </>
  );
};

const StatCard: React.FC<{
  icon: React.ReactNode;
  bg: string;
  label: string;
  value: number | string;
}> = ({ icon, bg, label, value }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 ${bg} rounded-lg flex items-center justify-center`}>{icon}</div>
      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p className="text-xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  </div>
);

const FieldInput: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
}> = ({ label, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
    />
  </div>
);

export default ContactsPage;
